import { isDefined, isNonEmpty, isNotDefined } from '@sgme/fp';
import { clsx } from 'clsx';
import type { ReactElement } from 'react';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { FlexiForwardFormField } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/form/FlexiForwardFormField.tsx';
import { PinnedExtra } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardHedgesForm/PinnedExtra.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { getFlexiForwardQuote } from '@/store/flexi-forward/helpers/rfs.ts';
import { useFlexiForwardPropertyChangedHandler } from '@/store/flexi-forward/hooks/useFlexiForwardPropertyChangedHandler.ts';
import { DEFAULT_HEDGE_TYPE } from '@/store/flexi-forward/state.ts';
import type { TcValidation } from '@/store/types.ts';

export function SpotRefField({ tileId, validation }: { tileId: string; validation?: TcValidation[] }): ReactElement {
  const { displayedSpotRef, isSpotHedgeType, isSpotRefDefined } = useFlexiForwardSelector((flexiForward) => {
    const selectedHedgeType = flexiForward.properties.hedgeType ?? DEFAULT_HEDGE_TYPE;
    const spotRef = flexiForward.properties.spotRef ?? undefined;

    const rfs = flexiForward.properties.rfs;
    const hedgeQuote = getFlexiForwardQuote(rfs)?.hedge;
    const spotRefQuote = isNotDefined(hedgeQuote?.price) ? undefined : Number.parseFloat(hedgeQuote.price);

    return {
      displayedSpotRef: spotRef ?? spotRefQuote,
      isSpotHedgeType: selectedHedgeType === 'Spot',
      isSpotRefDefined: isDefined(flexiForward.properties.spotRef) || isNonEmpty(flexiForward.properties.spotRef),
    };
  });

  const handleSpotRefChanged = useFlexiForwardPropertyChangedHandler<number | undefined>(
    tileId,
    'Spot ref. change',
    (spotRefValue) => ({ spotRef: isDefined(spotRefValue) ? spotRefValue : null }),
  );

  return (
    <FlexiForwardFormField
      formattedMessageId="flexiForwardTile.mainFields.spotRef.label"
      isDisplayed={isSpotHedgeType}
      extra={<PinnedExtra isPinned={isSpotRefDefined} onClick={() => handleSpotRefChanged(undefined)} />}
    >
      <TradeCaptureValidationWrapper validation={validation}>
        <SgNumberInput
          size="sm"
          value={displayedSpotRef}
          onChange={handleSpotRefChanged}
          className={clsx({ 'text-info fw-bold': isSpotRefDefined })}
          data-e2e="spot-ref"
        />
      </TradeCaptureValidationWrapper>
    </FlexiForwardFormField>
  );
}
